import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { CARD } from './Editor';

/**
 * Card 324 — who hears about guest feedback, and how often.
 *
 * Recipients are per company, not per questionnaire (Andy, 2026-09-30): one
 * head-office list hears about every tour. The emails themselves are card 326;
 * this only stores the choices. The backend rejects a malformed address rather
 * than dropping it, because a typo'd recipient sends every alert into a void.
 */

const CADENCES = [
  { value: 'immediate', label: 'Every time' },
  { value: 'daily', label: 'Once a day' },
  { value: 'weekly', label: 'Once a week' },
  { value: 'off', label: 'Off' }
];
const DIGESTS = CADENCES.filter(c => c.value !== 'immediate');

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_RECIPIENTS = 10;

const Choice = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2 mt-2" role="radiogroup">
    {options.map(option => (
      <button
        key={option.value}
        type="button"
        role="radio"
        aria-checked={value === option.value}
        onClick={() => onChange(option.value)}
        data-testid={testId ? `${testId}-${option.value}` : undefined}
        className={`text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid transition-colors ${
          value === option.value
            ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
        }`}>
        {option.label}
      </button>
    ))}
  </div>
);

Choice.propTypes = {
  options: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func,
  testId: PropTypes.string
};

const AlertSettings = ({ alerts, onSave, busy, error }) => {
  const [form, setForm] = useState(alerts);
  const [adding, setAdding] = useState('');
  const [addError, setAddError] = useState('');

  useEffect(() => setForm(alerts), [alerts]);

  const set = patch => setForm(current => ({ ...current, ...patch }));
  const dirty = JSON.stringify(form) !== JSON.stringify(alerts);

  const addRecipient = () => {
    const email = adding.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL.test(email)) {
      setAddError('That does not look like an email address.');
      return;
    }
    if (form.recipients.includes(email)) {
      setAdding('');
      return;
    }
    if (form.recipients.length >= MAX_RECIPIENTS) {
      setAddError(`Alerts can go to at most ${MAX_RECIPIENTS} people.`);
      return;
    }
    set({ recipients: [...form.recipients, email] });
    setAdding('');
    setAddError('');
  };

  return (
    <section className={CARD} data-testid="alert-settings">
      <h2 className="text-base font-black text-slate-900 m-0">Who hears about guest feedback</h2>
      <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
        Everyone here hears about every tour. Unhappy guests are the ones to hear about quickly,
        while there is still time to put it right.
      </p>

      <div className="mt-5">
        <span className="block text-sm font-bold text-slate-900">Send to</span>
        {form.recipients.length === 0 && (
          <p className="text-xs text-amber-700 m-0 mt-1">
            No one yet, so alerts have nowhere to go.
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {form.recipients.map(email => (
            <span
              key={email}
              className="inline-flex items-center gap-2 text-sm bg-slate-100 text-slate-800 rounded-xl pl-3 pr-1 py-1">
              {email}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                onClick={() => set({ recipients: form.recipients.filter(r => r !== email) })}
                className="text-slate-500 hover:text-slate-900 bg-transparent border-0 cursor-pointer text-base px-2">
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 mt-3 max-w-md">
          <input
            type="email"
            value={adding}
            placeholder="name@yourcompany.com"
            onChange={e => {
              setAdding(e.target.value);
              setAddError('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addRecipient();
              }
            }}
            aria-label="Add a recipient"
            data-testid="alert-add-input"
            className="flex-1 text-sm text-slate-900 placeholder-slate-400 border border-solid border-slate-300 rounded-xl px-3 py-2.5 focus:outline-none focus:border-indigo-500"
          />
          <button
            type="button"
            onClick={addRecipient}
            data-testid="alert-add"
            className="text-sm font-bold rounded-xl px-4 py-2.5 cursor-pointer border border-solid border-indigo-200 bg-white text-indigo-700">
            Add
          </button>
        </div>
        {addError && <p className="text-xs text-rose-700 m-0 mt-1">{addError}</p>}
      </div>

      <div className="mt-6">
        <span className="block text-sm font-bold text-slate-900">
          Unhappy guests (overall score {form.negativeAtOrBelow} or lower)
        </span>
        <Choice
          options={CADENCES}
          value={form.negative}
          onChange={negative => set({ negative })}
          testId="alert-negative"
        />
      </div>

      <div className="mt-6">
        <span className="block text-sm font-bold text-slate-900">
          Delighted guests (overall score {form.positiveAtOrAbove})
        </span>
        <Choice
          options={CADENCES}
          value={form.positive}
          onChange={positive => set({ positive })}
          testId="alert-positive"
        />
      </div>

      <div className="mt-6">
        <span className="block text-sm font-bold text-slate-900">
          Summary report with every response (PDF)
        </span>
        <Choice
          options={DIGESTS}
          value={form.digest}
          onChange={digest => set({ digest })}
          testId="alert-digest"
        />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 max-w-lg">
        <label className="block">
          <span className="block text-sm font-bold text-slate-900 mb-1">
            Unhappy means an overall score of
          </span>
          <select
            value={form.negativeAtOrBelow}
            onChange={e => set({ negativeAtOrBelow: Number(e.target.value) })}
            className="w-full text-sm border border-solid border-slate-300 rounded-xl px-3 py-2.5 bg-white">
            {[1, 2, 3].map(n => (
              <option key={n} value={n}>
                {n} or lower
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-bold text-slate-900 mb-1">
            Delighted means an overall score of
          </span>
          <select
            value={form.positiveAtOrAbove}
            onChange={e => set({ positiveAtOrAbove: Number(e.target.value) })}
            className="w-full text-sm border border-solid border-slate-300 rounded-xl px-3 py-2.5 bg-white">
            {[4, 5].map(n => (
              <option key={n} value={n}>
                {n === 5 ? '5 only' : `${n} or higher`}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p
          className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3 m-0 mt-4"
          role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={busy || !dirty}
        onClick={() => onSave(form)}
        data-testid="alert-save"
        className="mt-6 bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
        {busy ? 'Saving' : 'Save'}
      </button>
    </section>
  );
};

AlertSettings.propTypes = {
  alerts: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string
};

export default AlertSettings;
